import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector/DateRangeSelector';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { NextDateRangePresetNames } from 'src/utils/dateTimeUtils';
import { DateTimeRange } from 'src/WebApiController';

import { CreateEventGroupFormState } from '../EventGroup.types';
import * as styles from './EventGroupInfoStep.css';
import { EventGroupInfoStepOptions } from './EventGroupInfoStep.hooks';

type EventGroupInfoStepProps = {
  formState: CreateEventGroupFormState;
  onChange: (partial: Partial<CreateEventGroupFormState>) => void;
  errors: Partial<Record<keyof CreateEventGroupFormState, string>>;
  currencyCode: string;
  /** Venue/category search state + options, owned by the parent so it survives
   * navigating between steps. */
  options: EventGroupInfoStepOptions;
};

export const EventGroupInfoStep = ({
  formState,
  onChange,
  errors,
  currencyCode,
  options,
}: EventGroupInfoStepProps) => {
  // Placeholders need resolved strings (the inputs take a plain string prop).
  const searchVenuePlaceholder = useContent(ContentId.SearchVenue);
  const searchCategoryPlaceholder = useContent(ContentId.SearchCategory);

  const {
    venueSearchText,
    onVenueSearchChange,
    isVenueLoading,
    venueOptions,
    selectVenue,
    categorySearchText,
    onCategorySearchChange,
    isCategoryLoading,
    categoryOptions,
    selectCategory,
  } = options;

  return (
    <Stack direction="column" gap="l" width="full">
      {/* Row 1: Name + Number of Events */}
      <Stack direction="row" gap="l" width="full">
        <Stack direction="column" gap="m" className={styles.field}>
          <PosFormField
            label={<Content id={ContentId.Name} />}
            errors={errors.name}
          >
            <PosTextField
              value={formState.name}
              onChange={(e) => onChange({ name: e.target.value })}
            />
          </PosFormField>
        </Stack>
        <Stack direction="column" gap="m" className={styles.field}>
          <PosFormField
            label={<Content id={ContentId.NumberOfEvents} />}
            errors={errors.expectedEventCount}
          >
            <PosTextField
              type="number"
              min={1}
              value={String(formState.expectedEventCount)}
              onChange={(e) =>
                onChange({
                  expectedEventCount: Math.max(1, Number(e.target.value)),
                })
              }
              placeholder="1"
            />
          </PosFormField>
        </Stack>
      </Stack>
      {/* Row 2: Total Cost */}
      <Stack direction="row" gap="l" width="full">
        <Stack direction="column" gap="m" className={styles.field}>
          <PosFormField
            label={<Content id={ContentId.TotalCost} />}
            errors={errors.totalCost}
          >
            <PosTextField
              type="number"
              min={0}
              step="0.01"
              value={
                formState.totalCost != null ? String(formState.totalCost) : ''
              }
              onChange={(e) =>
                onChange({
                  totalCost: e.target.value ? Number(e.target.value) : null,
                })
              }
              placeholder="0.00"
            />
          </PosFormField>
          {formState.totalCost != null && formState.expectedEventCount > 0 && (
            <div className={styles.hint}>
              <FormatContent
                id={FormatContentId.CostPerEventAmount}
                params={[
                  (formState.totalCost / formState.expectedEventCount).toFixed(
                    2
                  ),
                  currencyCode,
                ]}
              />
            </div>
          )}
        </Stack>
        <Stack direction="column" gap="m" className={styles.field}>
          <PosFormField
            label={<Content id={ContentId.DateRange} />}
            errors={errors.startDate}
          >
            <DateRangeSelector
              value={
                formState.startDate != null || formState.endDate != null
                  ? ({
                      start: formState.startDate,
                      end: formState.endDate,
                    } as DateTimeRange)
                  : null
              }
              onChange={({ value }) => {
                const range = value as DateTimeRange | null;
                onChange({
                  startDate: range?.start ?? null,
                  endDate: range?.end ?? null,
                });
              }}
              presetNames={NextDateRangePresetNames}
            />
          </PosFormField>
        </Stack>
      </Stack>

      {/* Row 3: Venue + Category */}
      <Stack direction="row" gap="l" width="full">
        <Stack direction="column" gap="m" className={styles.field}>
          <PosFormField
            label={<Content id={ContentId.Venue} />}
            errors={errors.venueId}
          >
            <PosSelect
              showFullWidth
              searchable
              loading={isVenueLoading}
              searchInput={venueSearchText}
              hasNextPage
              onSearchInputChange={onVenueSearchChange}
              value={
                formState.venueId != null
                  ? String(formState.venueId)
                  : undefined
              }
              valueOptionsContent={venueOptions}
              onChange={(val) => {
                const venue = selectVenue(val ? Number(val) : null);
                onChange({
                  venueId: val ? Number(val) : null,
                  venueName: venue?.name ?? null,
                  venueLocation: venue
                    ? [venue.city, venue.state].filter(Boolean).join(' ') ||
                      null
                    : null,
                  venueConfigId: null,
                });
              }}
              placeholderText={searchVenuePlaceholder}
            />
          </PosFormField>
        </Stack>
        <Stack direction="column" gap="m" className={styles.field}>
          <PosFormField
            label={<Content id={ContentId.Performer} />}
            errors={errors.categoryId}
          >
            <PosSelect
              showFullWidth
              searchable
              loading={isCategoryLoading}
              searchInput={categorySearchText}
              hasNextPage
              onSearchInputChange={onCategorySearchChange}
              value={
                formState.categoryId != null
                  ? String(formState.categoryId)
                  : undefined
              }
              valueOptionsContent={categoryOptions}
              onChange={(val) => {
                selectCategory(val ? Number(val) : null);
                onChange({ categoryId: val ? Number(val) : null });
              }}
              placeholderText={searchCategoryPlaceholder}
            />
          </PosFormField>
        </Stack>
      </Stack>
    </Stack>
  );
};
